import { Link } from "react-router-dom";
import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";
import type { Farm } from "@/mocks/farms";

export default function FarmDetailOverview({ farm }: { farm: Farm }) {
  const facts = [
    { label: "Farmer", value: farm.farmer },
    { label: "Village", value: farm.village },
    { label: "District", value: farm.district },
    { label: "Main crop", value: farm.crop },
    { label: "Farm size", value: farm.area },
    { label: "Established", value: farm.established },
  ];

  return (
    <section className="sun-glaze bg-background-50 py-20 md:py-24">
      <div className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <Reveal className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
              <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
                About This Farm
              </span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl leading-tight text-foreground-950 font-semibold">
              A Working Farm You Can Walk Through
            </h2>
            <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">
              {farm.summary} Every Rythugaru farm is a real, working family farm — nothing staged,
              nothing themed. You visit on the terms of the family who lives and works here.
            </p>

            <h3 className="font-heading text-xl md:text-2xl text-foreground-950 font-semibold mt-9">
              What You'll Do Here
            </h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
              {farm.highlights.map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-3 bg-background-100 border border-background-200 rounded-md px-4 py-3"
                >
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 shrink-0">
                    <i className="ri-check-line text-lg"></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-800">{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal className="lg:col-span-5" delay={120}>
            <div className="bg-secondary-50 rounded-lg border border-secondary-200 p-7 md:p-8">
              <h3 className="font-heading text-xl text-foreground-950 font-semibold">
                Farm at a Glance
              </h3>
              <dl className="mt-5 divide-y divide-secondary-200">
                {facts.map((fact) => (
                  <div key={fact.label} className="flex items-center justify-between gap-4 py-3">
                    <dt className="text-sm text-foreground-600">{fact.label}</dt>
                    <dd className="text-sm font-semibold text-foreground-950 text-right">
                      {fact.value}
                    </dd>
                  </div>
                ))}
              </dl>

              <div className="pt-6 mt-2 border-t border-secondary-200">
                <p className="text-xs font-semibold uppercase tracking-wider text-accent-600 mb-2">
                  Experience it in person
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  {farm.experienceLabel} is hosted right here at {farm.name}.
                </p>
                <Link
                  to={`/experiences/${farm.experienceSlug}`}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-6 py-3.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  View the Experience
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}